import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import { GeneratedDramaProject, ScriptScene, formatDramaProjectForExport, getLatestDramaProject, saveLatestDramaProject } from '../services/dramaProjectStore';

const emptyScene: ScriptScene = {
  id: '',
  title: '',
  description: '',
  duration: '',
  location: '',
  characters: [],
  shotType: '中景',
  cameraMovement: '固定',
  lighting: '自然光',
  sound: '',
  dialogue: '',
  notes: '',
};

const StoryboardPage: React.FC = () => {
  const [scenes, setScenes] = useState<ScriptScene[]>([]);
  const [projectTitle, setProjectTitle] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingScene, setEditingScene] = useState<ScriptScene | null>(null);
  const [formScene, setFormScene] = useState<ScriptScene>(emptyScene);
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    const latestProject = getLatestDramaProject();
    if (latestProject?.scriptScenes?.length) {
      setScenes(latestProject.scriptScenes);
      setProjectTitle(latestProject.title);
    }
  }, []);

  const syncProjectScenes = (nextScenes: ScriptScene[]) => {
    const latestProject = getLatestDramaProject();
    if (!latestProject) {
      return;
    }

    saveLatestDramaProject({
      ...latestProject,
      scriptScenes: nextScenes,
    });
  };

  const resetForm = () => {
    setEditingScene(null);
    setFormScene(emptyScene);
    setShowAddForm(false);
  };

  const handleAddScene = () => {
    if (!formScene.title.trim() || !formScene.description.trim()) {
      setStatusMessage('至少补齐镜头标题和镜头描述。');
      return;
    }

    const nextScene = {
      ...formScene,
      id: formScene.id || `${Date.now()}`,
    };

    if (editingScene) {
      const nextScenes = scenes.map((scene) => (scene.id === editingScene.id ? nextScene : scene));
      setScenes(nextScenes);
      syncProjectScenes(nextScenes);
      setStatusMessage(`已更新镜头：${nextScene.title}`);
    } else {
      const nextScenes = [...scenes, nextScene];
      setScenes(nextScenes);
      syncProjectScenes(nextScenes);
      setStatusMessage(`已新增镜头：${nextScene.title}`);
    }

    resetForm();
  };

  const handleEditScene = (scene: ScriptScene) => {
    setEditingScene(scene);
    setFormScene(scene);
    setShowAddForm(true);
  };

  const handleDeleteScene = (sceneId: string) => {
    if (!confirm('确定要删除这个镜头吗？')) {
      return;
    }

    const nextScenes = scenes.filter((scene) => scene.id !== sceneId);
    setScenes(nextScenes);
    syncProjectScenes(nextScenes);
    setStatusMessage('镜头已删除。');
  };

  const handleLoadFromLatestScript = () => {
    const latestProject = getLatestDramaProject();
    if (!latestProject?.scriptScenes?.length) {
      setStatusMessage('还没有可同步的分镜，请先在创作中心生成剧本。');
      return;
    }

    setScenes(latestProject.scriptScenes);
    setProjectTitle(latestProject.title);
    setStatusMessage(`已从《${latestProject.title}》同步 ${latestProject.scriptScenes.length} 个镜头。`);
  };

  const getTotalDuration = () => {
    const totalSeconds = scenes.reduce((sum, scene) => {
      const [minutes, seconds] = scene.duration.split(':').map((value) => parseInt(value || '0', 10));
      return sum + minutes * 60 + seconds;
    }, 0);

    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleExport = () => {
    const latestProject = getLatestDramaProject();
    if (!latestProject || scenes.length === 0) {
      setStatusMessage('暂无分镜内容可以导出。');
      return;
    }

    const exportProject: GeneratedDramaProject = {
      ...latestProject,
      scriptScenes: scenes,
    };

    const blob = new Blob([formatDramaProjectForExport(exportProject)], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${exportProject.title || 'AI短剧分镜脚本'}.txt`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    setStatusMessage('分镜脚本已导出。');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-8">
          <Link to="/" className="hover:text-purple-600">首页</Link>
          <span>/</span>
          <span className="text-gray-900">分镜设计</span>
        </nav>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">分镜设计</h1>
          <p className="text-gray-600">把最新剧本拆成镜头、场景和对白，方便继续拍摄。</p>
          <div className="mt-4 flex flex-wrap items-center gap-6 text-sm text-gray-600">
            <span>📹 总镜头数: {scenes.length}</span>
            <span>⏱️ 总时长: {getTotalDuration()}</span>
            <span>🎭 涉及角色: {Array.from(new Set(scenes.flatMap((scene) => scene.characters))).length}</span>
            {projectTitle && <span>📝 当前项目: {projectTitle}</span>}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
          >
            ➕ 手动添加镜头
          </button>
          <button
            onClick={handleLoadFromLatestScript}
            className="px-6 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-colors"
          >
            🎬 从当前剧本生成分镜
          </button>
          <Link
            to="/create"
            className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors text-center"
          >
            📝 返回剧本创作
          </Link>
          <button
            onClick={handleExport}
            className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
          >
            📥 导出分镜脚本
          </button>
        </div>

        {statusMessage && (
          <div className="mb-6 rounded-lg bg-purple-50 px-4 py-3 text-sm text-purple-700">
            {statusMessage}
          </div>
        )}

        {showAddForm && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              {editingScene ? '编辑镜头' : '添加新镜头'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">镜头标题</label>
                <input
                  type="text"
                  value={formScene.title}
                  onChange={(e) => setFormScene({ ...formScene, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="请输入镜头标题"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">时长</label>
                <input
                  type="text"
                  value={formScene.duration}
                  onChange={(e) => setFormScene({ ...formScene, duration: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="例: 0:30"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">拍摄地点</label>
                <input
                  type="text"
                  value={formScene.location}
                  onChange={(e) => setFormScene({ ...formScene, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="请输入拍摄地点"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">涉及角色</label>
                <input
                  type="text"
                  value={formScene.characters.join('、')}
                  onChange={(e) =>
                    setFormScene({
                      ...formScene,
                      characters: e.target.value
                        .split(/[、,，]/)
                        .map((item) => item.trim())
                        .filter(Boolean),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="用顿号或逗号分隔多个角色"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">镜头类型</label>
                <select
                  value={formScene.shotType}
                  onChange={(e) => setFormScene({ ...formScene, shotType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="特写">特写</option>
                  <option value="近景">近景</option>
                  <option value="中景">中景</option>
                  <option value="全景">全景</option>
                  <option value="远景">远景</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">镜头运动</label>
                <select
                  value={formScene.cameraMovement}
                  onChange={(e) => setFormScene({ ...formScene, cameraMovement: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="固定">固定</option>
                  <option value="推镜头">推镜头</option>
                  <option value="拉镜头">拉镜头</option>
                  <option value="摇镜头">摇镜头</option>
                  <option value="移镜头">移镜头</option>
                  <option value="环绕">环绕</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">镜头描述</label>
                <textarea
                  value={formScene.description}
                  onChange={(e) => setFormScene({ ...formScene, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 h-20 resize-none"
                  placeholder="请输入镜头描述"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">灯光要求</label>
                <input
                  type="text"
                  value={formScene.lighting}
                  onChange={(e) => setFormScene({ ...formScene, lighting: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="例: 温暖的自然光"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">声音效果</label>
                <input
                  type="text"
                  value={formScene.sound}
                  onChange={(e) => setFormScene({ ...formScene, sound: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="例: 轻柔的背景音乐"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">关键对话</label>
                <textarea
                  value={formScene.dialogue}
                  onChange={(e) => setFormScene({ ...formScene, dialogue: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 h-16 resize-none"
                  placeholder="请输入关键对话"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">备注</label>
                <textarea
                  value={formScene.notes}
                  onChange={(e) => setFormScene({ ...formScene, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 h-16 resize-none"
                  placeholder="请输入备注信息"
                />
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <button
                onClick={handleAddScene}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                {editingScene ? '确认更新' : '确认添加'}
              </button>
              <button
                onClick={resetForm}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                取消
              </button>
            </div>
          </div>
        )}

        {scenes.length > 0 ? (
          <div className="space-y-6">
            {scenes.map((scene, index) => (
              <div key={scene.id} className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-purple-600 text-white flex items-center justify-center font-semibold shrink-0">
                      {index + 1}
                    </div>
                    <div>
                      <h3 className="text-2xl font-semibold text-gray-900">{scene.title}</h3>
                      <p className="text-sm text-gray-500 mt-1">⏱ {scene.duration} ｜ 📍 {scene.location}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditScene(scene)}
                      className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      编辑
                    </button>
                    <button
                      onClick={() => handleDeleteScene(scene.id)}
                      className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                    >
                      删除
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">镜头描述</h4>
                    <p className="text-gray-600">{scene.description}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">技术参数</h4>
                    <ul className="text-gray-600 space-y-1 text-sm">
                      <li>📹 镜头类型: {scene.shotType}</li>
                      <li>🎥 镜头运动: {scene.cameraMovement}</li>
                      <li>💡 灯光: {scene.lighting}</li>
                      <li>🔊 声音: {scene.sound}</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">角色与对白</h4>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {scene.characters.map((character) => (
                        <span key={character} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                          {character}
                        </span>
                      ))}
                    </div>
                    <p className="text-gray-600 text-sm">“{scene.dialogue}”</p>
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-4">
                  <h4 className="font-medium text-gray-900 mb-2">备注</h4>
                  <p className="text-gray-600 text-sm">{scene.notes}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <div className="text-5xl mb-4">🎬</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">还没有分镜内容</h3>
            <p className="text-gray-600 mb-6">先去创作中心生成一版剧本，再回来一键拆分镜。</p>
            <Link
              to="/create"
              className="inline-flex px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              去生成剧本
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoryboardPage;
